import { useMemo } from 'react';
import { OrderItemClaimedByHour } from '@mews-commander/data-access';
import { DateTimeService, DateTimeUtcService, useAppClockService, useLocalizeText, useTimezone } from '@mews/framework';
import { UnicodeEntity } from '@mews/enums';
import { TextKey } from '@mews-commander/enums';
import { ChartData, ProgressCharts } from '@mews/b2b-ui';

export const useClaimedByHourChartDataCategories = (
    claimedByHour: OrderItemClaimedByHour | undefined
): ProgressCharts[] => {
    const localizeText = useLocalizeText();
    const appClockService = useAppClockService();
    const timezone = useTimezone();

    return useMemo(() => {
        if (!claimedByHour) {
            return [];
        }

        const now = appClockService.getAppNowUtc().value;

        const values: ChartData[] = claimedByHour.claimedByHourCount
            .map((value) => ({ ...value, startHour: getStartHour(now, value.hourUtc, timezone) }))
            .sort((a, b) => a.startHour - b.startHour)
            .map((value, index) => ({
                name: `${value.startHour}${UnicodeEntity.Dash}${value.startHour + 1}`,
                id: index.toString(),
                value: value.count,
            }));

        if (claimedByHour.unclaimedCount) {
            values.push({
                name: localizeText({ textKey: TextKey.Unclaimed }),
                id: 'unclaimed',
                value: claimedByHour.unclaimedCount,
                color: 'disabled',
            });
        }

        return values.length
            ? [
                  {
                      data: values,
                      total: values.reduce((acc, item) => acc + item.value, 0),
                      id: 'claimed-by-hour',
                      legend: {
                          showValues: true,
                      },
                  },
              ]
            : [];
    }, [appClockService, localizeText, claimedByHour, timezone]);
};

const getStartHour = (startUtc: string, hourUtc: number, timezone: string) => {
    const utc = DateTimeUtcService.parse(startUtc);
    const utcStart = DateTimeUtcService.startOf(utc, 'day');
    const finalUtc = DateTimeUtcService.addHours(utcStart, hourUtc);
    const localTime = DateTimeService.fromUtc(finalUtc, timezone);
    return DateTimeService.getHours(localTime);
};
